import { forwardRef, type ButtonHTMLAttributes, type ElementType, type ReactNode } from 'react';
import { Icon, type IconName } from './icon';
import { focusRing } from './focus-ring';
import styles from './icon-button.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Glyph to render. Omit and pass `children` for a custom inner. */
  icon?: IconName;
  /** Accessible name — pass an already-translated string. */
  label: string;
  size?: number;
  /**
   * `overlay` is the forced-dark mode, for surfaces that paint their own
   * dark ground regardless of theme (Cook Mode). Those cannot use the ghost
   * colours — the tokens track `data-theme`, not the local `color-scheme`,
   * so ghost renders dark ink on a dark overlay.
   */
  variant?: 'ghost' | 'surface' | 'overlay';
  active?: boolean;
  /** Overlay count (e.g. unread bell). Hidden when null/0. */
  badge?: number | string | null;
  /**
   * Render as a different element (e.g. Next `Link`) — receives `href` and the
   * rest of the props. Same escape hatch as `MenuItem`; needed because some
   * icon controls are navigation (RangeNav's prev/next), not buttons.
   */
  as?: ElementType;
  href?: string;
  children?: ReactNode;
}

/**
 * Square ≥44px touch icon control — one definition for every header /
 * toolbar / overlay icon action. `variant` ghost | surface | overlay, optional `active`
 * state and count `badge`. Ported from the #302 element library (`IconButton`).
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    { icon, label, size = 18, variant = 'ghost', active = false, badge, as, className, children, ...rest },
    ref,
  ) {
    const Comp = as ?? 'button';
    // `type` is meaningless (and invalid) on an anchor — only stamp it when
    // this really is a button.
    const ownProps = as ? {} : { type: 'button' as const };
    return (
      <Comp
        ref={ref}
        aria-label={label}
        className={cx(
          styles.iconBtn,
          variant === 'surface' && styles.surface,
          variant === 'overlay' && styles.overlay,
          active && styles.active,
          focusRing,
          className,
        )}
        {...ownProps}
        {...rest}
      >
        {icon ? <Icon name={icon} size={size} strokeWidth={1.75} aria-hidden="true" /> : children}
        {badge != null && badge !== 0 ? <span className={styles.badge}>{badge}</span> : null}
      </Comp>
    );
  },
);
